package org.multiplatform.kotfolio.pages.compose.side_effect

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/side_effect/derivedstateof")
@Composable
fun DerivedStateOfPage() {
    BlogLayout(
        PageProperties(
            "derivedStateOf",
            "derivedStateOf is used to create a state that is derived from another state, ensuring that the derived state only recomputes when necessary."
        ),
        BlogProperties("Jetpack Compose", "derivedStateOf"))
    {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "The Problem: Recomposition Churn",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em).fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When a state object updates rapidly (like scroll offsets, text changes, or animations), calculating a derived condition directly inside the composable body triggers recompositions on every single tick — even when the calculated outcome has not changed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "The Solution",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em).fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "derivedStateOf creates a derived state object that recalculates only when its observed inputs change, and notifies downstream observers only when the computed output value flips.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Storage Location: Derived State Wrapper",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "derivedStateOf is used to create a state that is derived from another state, ensuring that the derived state only recomputes when necessary (Create a new state based on existing state objects, minimizing unnecessary recompositions.), thus improving performance. MutableStateOf is used for creating a state that can be directly read and modified. DerivedStateOf is used for creating state that is derived from other state(s).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "derivedStateOf is used to create a state that is derived from another state, ensuring that the derived state only recomputes when necessary (Create a new state based on existing state objects, minimizing unnecessary recompositions.), thus improving performance. MutableStateOf is used for creating a state that can be directly read and modified. DerivedStateOf is used for creating state that is derived from other state(s).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "DerivedStateOf allows creating a state object that is derived based on a specific input state. Allows you to derive a new state value based on multiple other states. (This feature is useful in cases where multiple states are interdependent)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "DerivedStateOf acts as a lambda function that takes one or more state objects as input. The lambda function uses one or more input states to create a new derived state object.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "derivedStateOf is a function in jetpack compose that is used to compute value based on the value of other states or derivedState, and caches the result.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "derivedStateOf internally wraps the result in a MutableState with a mutation policy. By default, it uses structuralEqualityPolicy() — the same as mutableStateOf.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "derivedStateOf not recommended: ViewModel logic should use Flow, StateFlow, or LiveData. derivedStateOf is meant for UI-level computations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Work: derivedStateOf {} should be used when your state or key is changing more than you want to update your UI. Or in other words, derivedStateOf is like distinctUntilChanged from Kotlin Flows or other similar reactive frameworks. Remember that Composables recompose when the Compose state object they read, changes. derivedStateOf allows you to create a new state object that changes only as much as you need.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It is similar to remember, but instead of holding a piece of state directly, it computes a derived value based on other state objects. For example filter.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The calculation is expensive (sorting, filtering large lists).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "You want to ensure the calculation happens only when dependencies change.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "You’re working with state flows or other complex state sources.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The operation involves multiple state inputs that change independently.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It is useful when you need to compute a value that depends on other values, and you want to avoid recomputing the value unnecessarily. (If you have state that is derived from other state values, use derivedStateOf to optimize when this derived state is recalculated. derivedStateOf ensures that the derived value is only recomputed when its source state changes.)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The sum is only recalculated when the list changes. If other parts of the Composable trigger a recomposition, the sum remains unchanged unless its dependencies change.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "When to Use derivedStateOf",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "derivedStateOf produces state you read during composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "derivedStateOf and Pre-computed Data",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Avoiding Recomposition: When a derived value depends on a frequently changing state, but you only want recomposition when the derived value changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "derivedStateOf vs Pre-computed Data",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "derivedStateOf (Composables)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "UI-specific logic - Search filtering, form validation, toolbar hide/show on scroll,  Showing/hiding floating action button, dynamic offers on scroll and conditional styling",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Performance optimisation - When multiple states change, but the derived result often stays the same",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Complex UI conditions - Combining loading + error + success states for button appearance",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Expensive transformations - Chart data, formatting, layout calculations",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Real-time derivations - Search results, dynamic theming, responsive layouts",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "PRE-COMPUTED DATA (ViewModel)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Core business logic - Pricing, discounts, tax calculations",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Data processing - API responses, complex filtering, aggregations",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Cross-screen logic - Data that multiple screens need",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Cacheable results - Expensive operations that should persist",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun DerivedStateExample(
                        items: List<String>,
                        searchQuery: String
                    ) {
                        //filteredItems is derived from items and searchQuery
                        //derivedStateOf ensures the list is only recalculated when searchQuery or items changes.
                        val filteredItems by remember(searchQuery, items) {
                            derivedStateOf {
                                items.filter {
                                    it.contains(searchQuery, ignoreCase = true)
                                }
                            }
                        }

                        Column {
                            Text("Search results:")

                            filteredItems.forEach { item ->
                                Text(item)
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/Dev-Aditya-More/PokeVerse.git
            KotlinCode(
                code = """
                    //LazyColumn Animation 
                    
                    Box(modifier = Modifier.fillMaxSize()) {
                        val animatedIndices = remember { mutableStateSetOf<Int>() }

                        // Compute visible item indices using derivedStateOf
                        val visibleIndices by remember {
                            derivedStateOf {
                                listState.layoutInfo.visibleItemsInfo.map { it.index }.toSet()
                            }
                        }

                        LazyColumn(
                            state = listState,
                            contentPadding = PaddingValues(16.dp),
                            verticalArrangement = Arrangement.spacedBy(12.dp),
                            modifier = Modifier.fillMaxSize()
                        ) {
                            itemsIndexed(pokemonList) { index, pokemon ->
                                LaunchedEffect(index, visibleIndices) {
                                    if (visibleIndices.contains(index)) {
                                        animatedIndices.add(index)
                                    }
                                }
                                // Stagger delay
                                val itemAnimDelay = index * 10L
                                var isVisible by remember { mutableStateOf(false) }

                                // Animate entry
                                val alphaa by animateFloatAsState(
                                    targetValue = if (isVisible) 1f else 0f,
                                    animationSpec = tween(durationMillis = 400, delayMillis = itemAnimDelay.toInt()),
                                    label = "alphaAnim"
                                )

                                val transY by animateDpAsState(
                                    targetValue = if (isVisible) 0.dp else 20.dp,
                                    animationSpec = tween(durationMillis = 400, delayMillis = itemAnimDelay.toInt()),
                                    label = "slideAnim"
                                )

                                // Trigger entry when composed
                                LaunchedEffect(Unit) {
                                    isVisible = true
                                }

                                Card(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .graphicsLayer {
                                            alpha = alphaa
                                            translationY = transY.toPx()
                                        }
                                        .clickable { },
                                    elevation = CardDefaults.cardElevation(4.dp),
                                    colors = CardDefaults.cardColors(containerColor = Color.Black)
                                ) {

                                }
                            }

                            if (isLoading && pokemonList.isNotEmpty()) {
                                item {
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .padding(16.dp),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        CustomProgressIndicator()
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //validation
                    
                    @Composable
                    fun ProfileForm(
                        // Imagine these are hoisted states from a ViewModel
                        firstNameState: State<String>,
                        emailState: State<String>,
                        ageState: State<String>
                    ) {
                        // Function to check if a string is a valid email (placeholder for expensive logic)
                        fun isEmailValid(email: String) = android.util.Patterns.EMAIL_ADDRESS.matcher(email).matches()

                        // 🌟 The powerful part: Derived state for overall form validity
                        // The lambda runs on every keystroke, but the 'isEnabled' State *value* only changes
                        // when the boolean result flips.
                        val isFormValid by remember {
                            derivedStateOf {
                                val isNameOk = firstNameState.value.isNotBlank() && firstNameState.value.length >= 2
                                val isEmailOk = isEmailValid(emailState.value)
                                val isAgeOk = ageState.value.toIntOrNull() in 18..99 // Check if it's a number in range
                                
                                // The derived result: true only if ALL are true
                                isNameOk && isEmailOk && isAgeOk
                            }
                        }

                        Column {
                            // ... TextFields for input (recompose on every keystroke)

                            Spacer(modifier = Modifier.height(32.dp))

                            // This Button Composable (and the Text) only recomposes when isFormValid.value changes,
                            // which is less frequently than any individual state change.
                            Button(
                                onClick = { /* Save action */ },
                                enabled = isFormValid
                            ) {
                                Text("Save Profile")
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/cinkhangin/composable
            KotlinCode(
                code = """
                    //Parallax Card
                    val scrollState = rememberLazyListState()
                     
                    fun LazyListScope.ParallaxCardStack(
                        cardItems: List<*>,
                        state: LazyListState
                    ) {
                        itemsIndexed(cardItems) { index, card ->
                            val isCurrentItem by remember {
                                derivedStateOf { index == state.firstVisibleItemIndex }
                            }

                            val isUnderStack by remember {
                                derivedStateOf { index < state.firstVisibleItemIndex }
                            }

                            val offset by remember {
                                derivedStateOf {
                                    if (isCurrentItem) {
                                        state.firstVisibleItemScrollOffset.toFloat()
                                    } else {
                                        0f
                                    }
                                }
                            }

                            val scale by remember {
                                derivedStateOf {
                                    val progress = state.layoutInfo.visibleItemsInfo.firstOrNull()?.let {
                                        offset / it.size.toFloat()
                                    } ?: 0f

                                    when {
                                        isUnderStack -> 0f
                                        isCurrentItem -> 1f - (progress * 0.1f)
                                        else -> 1f
                                    }
                                }
                            }

                            //UI        
                            Modifier
                                  .fillMaxWidth()
                                  .aspectRatio(1.5f)
                                  .graphicsLayer {
                                      translationY = offset
                                      scaleX = scale
                                      scaleY = scale
                                  }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/Moscow-Squad/CineVerse.git
            KotlinCode(
                code = """
                    @Immutable
                    @Stable
                    data class ExploreScreenState(
                       val shouldShowGenres: Boolean = true,
                    ) { }
                    
                    //ViewModel
                    val shouldShowGenres: Boolean = true,
                    
                    @OptIn(ExperimentalSharedTransitionApi::class)
                    @Composable
                    fun ExploreMainContent(
                        uiState: ExploreScreenState,
                        gridState: LazyGridState,
                        modifier: Modifier = Modifier,
                        onGenresVisibilityChange: (Boolean) -> Unit = {}
                    ) {
                        var lastScrollOffset by remember { mutableFloatStateOf(0f) }
                        var isScrollingDown by remember { mutableStateOf(false) }

                        val shouldShowGenres by remember {
                            derivedStateOf {
                                val firstVisibleItemIndex = gridState.firstVisibleItemIndex
                                val firstVisibleItemScrollOffset = gridState.firstVisibleItemScrollOffset

                                if (firstVisibleItemIndex == 0 && firstVisibleItemScrollOffset < 100) {
                                    true
                                } else if (isScrollingDown) {
                                    false
                                } else {
                                    true
                                }
                            }
                        }

                        LaunchedEffect(gridState) {
                            snapshotFlow { gridState.firstVisibleItemScrollOffset.toFloat() + gridState.firstVisibleItemIndex * 1000f }
                                .distinctUntilChanged()
                                .collect { currentOffset ->
                                    isScrollingDown = currentOffset > lastScrollOffset
                                    lastScrollOffset = currentOffset
                                    onGenresVisibilityChange(shouldShowGenres && uiState.shouldShowGenres)
                                }
                        }
                    }
                    
                    val gridState = rememberLazyGridState()
                    val genresState = rememberLazyListState()

                    var searchBarVisible by rememberSaveable { mutableStateOf(true) }
                    var genresVisible by rememberSaveable { mutableStateOf(true) }

                    LaunchedEffect(uiState.selectedTab) {
                        gridState.animateScrollToItem(0)
                        val genreId = if (uiState.selectedTab == ExploreTabsPages.MOVIES) {
                            uiState.selectedMovieGenre
                        } else {
                            uiState.selectedSeriesGenre
                        }
                        val targetIndex = uiState.genres.indexOfFirst { it.id == genreId }
                        if (targetIndex >= 0) genresState.animateScrollToItem(targetIndex)
                            searchBarVisible = true
                            genresVisible = true
                    }

                    LaunchedEffect(uiState.shouldShowGenres) {
                        if (uiState.shouldShowGenres) {
                            genresVisible = true
                            searchBarVisible = true
                        }
                    }
                    
                    @Composable
                    fun ExploreSearchBarSection(
                        uiState: ExploreScreenState,
                        interactionListener: ExploreInteractionListener,
                        modifier: Modifier = Modifier,
                        isVisible: Boolean = true
                    ) {
                        val context = LocalContext.current
                        val focusState = remember { mutableStateOf(false) }

                        BackHandler(enabled = uiState.showSuggestions) {
                            interactionListener.onCancelButtonClicked()
                            focusState.value = false
                        }

                        // Animated height for collapse effect
                        val height by animateDpAsState(
                            targetValue = if (isVisible) 56.dp else 0.dp,
                            animationSpec = tween(
                                durationMillis = 300,
                                easing = androidx.compose.animation.core.FastOutSlowInEasing
                            ),
                            label = "searchBarHeight"
                        )

                        // Animated alpha for smooth fade
                        val alpha by animateFloatAsState(
                            targetValue = if (isVisible) 1f else 0f,
                            animationSpec = tween(
                                durationMillis = 300,
                                easing = androidx.compose.animation.core.FastOutSlowInEasing
                            ),
                            label = "searchBarAlpha"
                        )

                        Box(
                            modifier = modifier
                                .fillMaxWidth()
                                .height(height)
                                .alpha(alpha)
                        ) {
                            if (height > 0.dp) {
                                
                            }
                        }
                    }
                    
                    ExploreMainContent(
                                        onGenresVisibilityChange = { shouldShow ->
                                            genresVisible = shouldShow
                                            searchBarVisible =
                                                if (uiState.searchKeyWord.isNotEmpty()) true else shouldShow
                                        },
                                        //sharedTransitionScope = this@SharedTransitionLayout,
                                        //animatedVisibilityScope = this@AnimatedContent
                                    )
                                    
                                    
                    """.trimIndent()
            )

            //https://github.com/bimovall/chess-player-viewer
            KotlinCode(
                code = """
                    var collapsedProfileFraction by remember { mutableStateOf(0F) }
                    ProfileScreen(
                        onCollapsedFractionChanged = {
                            collapsedProfileFraction = it
                        }
                    )
                    
                    @Composable
                    fun ProfileScreen(
                        onCollapsedFractionChanged: (Float) -> Unit,
                    ) {
                        val listState = rememberLazyListState()
                        var headerHeightPx by remember { mutableStateOf(0f) }
                        val collapseFraction by remember {
                            derivedStateOf {
                                if (headerHeightPx == 0f) 0f
                                else {
                                    val first = listState.firstVisibleItemIndex
                                    val offset = listState.firstVisibleItemScrollOffset.toFloat()
                                    if (first > 0) 1f
                                    else (offset / headerHeightPx).coerceIn(0f, 1f)
                                }
                            }
                        }

                        LaunchedEffect(collapseFraction) {
                            onCollapsedFractionChanged(collapseFraction)
                        }

                        LazyColumn(
                            state = listState,
                            modifier = Modifier
                                .padding(horizontal = 8.dp)
                        ) {
                            item {
                                Header(
                                    collapseFraction = 1f - collapseFraction,
                                    modifier = Modifier.onGloballyPositioned {
                                        headerHeightPx = it.size.height.toFloat()
                                    })
                            }
                        }
                    }
                    
                    @Composable
                    fun Avatar(modifier: Modifier = Modifier, path: String) {
                        SubcomposeAsyncImage(
                            model = ImageRequest.Builder(LocalPlatformContext.current)
                                .data(path)
                                .crossfade(true)
                                .build(),
                            contentDescription = "avatar",
                            error = {
                                Image(painter = painterResource(Res.drawable.ic_default_avatar), "")
                            },
                            loading = {
                                CircularProgressIndicator()
                            },
                            contentScale = ContentScale.Crop,
                            modifier = modifier .clip(CircleShape)
                        )
                    }
                    
                    @Composable
                    fun Header(
                        collapseFraction: Float,
                        modifier: Modifier = Modifier
                    ) {
                        Row(
                            modifier = modifier
                                .padding(vertical = 16.dp)
                                .graphicsLayer {
                                    alpha = collapseFraction
                                    scaleX = 0.8f + 0.2f * collapseFraction
                                    scaleY = 0.8f + 0.2f * collapseFraction
                                },
                            horizontalArrangement = Arrangement.spacedBy(16.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Avatar(
                                path = "",
                                modifier = Modifier.size(72.dp)
                            )
                            PersonalInformation(
                                title = "",
                                name = "",
                                username = "",
                                location = ""
                            )
                        }
                    }
                    
                    @Composable
                    fun PersonalInformation(
                        title: String,
                        name: String,
                        username: String,
                        location: String,
                        modifier: Modifier = Modifier
                    ) {
                        Column(modifier = modifier.wrapContentHeight()) {
                            Row {
                                BadgeItem(title, modifier = Modifier.align(Alignment.CenterVertically))
                                Spacer(modifier = Modifier.width(4.dp))
                                Text(
                                    username,
                                    style = MaterialTheme.typography.bodyMedium.copy(
                                        fontSize = 16.sp
                                    ),
                                    modifier = Modifier.align(Alignment.CenterVertically)
                                        .weight(1f)
                                )
                            }
                            Spacer(modifier = Modifier.height(8.dp))

                            Text(
                                name,
                                style = MaterialTheme.typography.bodyMedium.copy(
                                    color = Color.Black.copy(alpha = 0.5F)
                                ),
                            )
                            Spacer(modifier = Modifier.height(8.dp))

                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Icon(painter = painterResource(Res.drawable.ic_location), "")
                                Spacer(modifier = Modifier.width(4.dp))
                                Text(
                                    location,
                                    style = MaterialTheme.typography.bodyMedium.copy(
                                        fontSize = 14.sp
                                    ),
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val markers: SnapshotStateList<KrossMarker> = mutableStateListOf()
                    
                    val initialMarkers by remember {
                        derivedStateOf { mapState.markers.toList() }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //derivedStateOf remember(items)
                    val isButtonEnabled by remember(items) {
                        derivedStateOf {
                            items.any()
                        }
                    }
                    
                    //SnapshotStateList only by remember
                    val isButtonEnabled by remember {
                        derivedStateOf {
                            items.any()
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val buffer = 1
                    val listState: LazyListState = rememberLazyListState()

                    // observe list scrolling
                    val reachedBottom: Boolean by remember {
                        derivedStateOf {
                            val lastVisibleItem = listState.layoutInfo.visibleItemsInfo.lastOrNull()
                            lastVisibleItem?.index != 0 && lastVisibleItem?.index == listState.layoutInfo.totalItemsCount - buffer
                        }
                    }

                    // load more if scrolled to bottom
                    LaunchedEffect(reachedBottom) {
                        if (reachedBottom) {
                            loadPokemonList()
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val mainListState = rememberLazyListState()

                    @Composable
                    fun ListHorizontalProgressbar(modifier: Modifier = Modifier, scrollState: LazyListState) {
                        val totalItems = remember { derivedStateOf { scrollState.layoutInfo } }.value.totalItemsCount
                        val visibleItems = remember { derivedStateOf { scrollState.layoutInfo } }.value.visibleItemsInfo.size
                        val firstVisibleItem = remember { derivedStateOf { scrollState.firstVisibleItemIndex } }.value

                        if (totalItems > visibleItems && visibleItems > 0) {
                            val offset = firstVisibleItem.toFloat() / totalItems

                            Box(
                                modifier = modifier
                                    .height(2.dp)
                                    .fillMaxWidth()
                                    .drawBehind {
                                        val scrollbarWidth = size.width * offset

                                        drawRoundRect(
                                            color = LessonTopicStudyTheme.PRIMARY.toColor(),
                                            topLeft = Offset(x = 0f, y = 0f),
                                            size = Size(width = scrollbarWidth, height = size.height),
                                            cornerRadius = CornerRadius(2.dp.toPx())
                                        )
                                    }
                            ) {

                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/gonzalo-droid/mtc-quiz
            KotlinCode(
                code = $$"""
                    //Quiz ProgressBar
                    
                    val scrollState = rememberLazyListState()
                    var startItemVisible by remember { mutableIntStateOf(1) }
                    val progress by remember {
                        derivedStateOf {
                            //val totalItems = remember { derivedStateOf { scrollState.layoutInfo } }.value.totalItemsCount //stability
                      
                            val totalItems = scrollState.layoutInfo.totalItemsCount
                            val firstVisibleItem = scrollState.firstVisibleItemIndex
                            val lastVisibleItem = scrollState.layoutInfo.visibleItemsInfo.lastOrNull()?.index ?: 0

                            if (totalItems > 1) {
                                startItemVisible = firstVisibleItem.toInt() + 2
                                val normalizedProgress =
                                    lastVisibleItem.toFloat() / (totalItems - 1).coerceAtLeast(1)
                                normalizedProgress.coerceIn(0f, 1f)
                            } else {
                                0f
                            }
                        }
                    }
                    
                    LazyColumn(
                        state = scrollState, modifier = Modifier.weight(1f)
                    ) {  }
                    
                    LinearProgressComponent(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(bottom = 8.dp),
                        progress = progress,
                        countProgress = "${startItemVisible}/${state.questions.size}"
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    var expanded by remember { mutableStateOf(false) }
                    val maxLines by remember { derivedStateOf { if (expanded) Int.MAX_VALUE else 4 } }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //TrailList(modifier = Modifier.weight(1F))

                    @Composable
                    private fun TrailList(modifier: Modifier = Modifier) {
                        Box(
                            modifier = modifier
                                .fillMaxWidth(),
                        ) {
                            val listState = rememberLazyListState()
                            val firstVisibleItemIndex = remember {
                                derivedStateOf {
                                    listState.firstVisibleItemScrollOffset
                                }
                            }

                            LazyColumn(
                                contentPadding = PaddingValues(
                                    start = 16.dp,
                                    top = 0.dp,
                                    end = 16.dp,
                                    bottom = 16.dp,
                                ),
                                verticalArrangement = Arrangement.spacedBy(16.dp),
                                state = listState,
                                modifier = modifier
                                    .fillMaxSize(),
                            ) {

                            }

                            ExtendedFloatingActionButton(
                                onClick = {},
                                shape = RoundedCornerShape(50),
                                modifier = Modifier
                                    .padding(16.dp)
                                    .align(Alignment.BottomCenter),
                            ) {
                                Icon(
                                    imageVector = Icons.Default.Map,
                                    contentDescription = null,
                                )

                                AnimatedVisibility(
                                    visible = firstVisibleItemIndex.value == 0,
                                ) {
                                    Text(
                                        text = "Map",
                                        modifier = Modifier
                                            .padding(start = 8.dp),
                                    )
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val showScrollToTopButton by remember(isEnabled) {
                        derivedStateOf {
                            state.firstVisibleItemIndex >= 5 && isEnabled
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/Quantum3600/Optimite.git
            KotlinCode(
                code = """
                    //Scroll hide/show FAB coerceAtLeast
                    
                    // Derive stable list of ticker items to avoid recreating list on every recomposition
                    val tickerItems by derivedStateOf { tickerDataMap.values.toList() }
                        
                    // Optimize visible symbols calculation - use derivedStateOf for automatic recomposition only when needed
                    val visibleSymbols by derivedStateOf {
                        val layoutInfo = listState.layoutInfo
                        if (tickerItems.isEmpty() || layoutInfo.visibleItemsInfo.isEmpty()) {
                            return@derivedStateOf emptyList()
                        }

                        val prefetchBuffer = 10
                        val visibleIndices = layoutInfo.visibleItemsInfo.map { it.index }

                        val startIndex = visibleIndices.minOrNull()
                            ?.minus(prefetchBuffer)
                            ?.coerceAtLeast(0)
                            ?: 0

                        val endIndex = visibleIndices.maxOrNull()
                            ?.plus(prefetchBuffer)
                            ?.coerceAtMost(tickerItems.lastIndex)
                            ?: tickerItems.lastIndex

                        (startIndex..endIndex).mapNotNull { index ->
                            tickerItems.getOrNull(index)?.symbol
                        }
                    }
                    
                    @Composable
                    fun BoxScope.ScrollToEdgeButton(
                        listState: LazyListState,
                        totalItems: Int,
                        modifier: Modifier = Modifier
                    ) {
                        val scope = rememberCoroutineScope()

                        val isNearTop by remember {
                            derivedStateOf { listState.firstVisibleItemIndex <= 5 }
                        }
                        val hasVisibleItems by remember {
                            derivedStateOf { listState.layoutInfo.visibleItemsInfo.isNotEmpty() }
                        }
                        val visible = totalItems > 10 && hasVisibleItems

                        AnimatedVisibility(
                            visible = visible,
                            modifier = modifier
                                .align(Alignment.BottomEnd)
                                .padding(end = 24.dp, bottom = 16.dp),
                            enter = fadeIn() + scaleIn(),
                            exit = fadeOut() + scaleOut()
                        ) {
                            SmallFloatingActionButton(
                                onClick = {
                                    scope.launch {
                                        if (isNearTop) {
                                            listState.animateScrollToItem((totalItems - 1).coerceAtLeast(0))
                                        } else {
                                            listState.animateScrollToItem(0)
                                        }
                                    }
                                },
                                containerColor = MaterialTheme.colorScheme.secondaryContainer,
                                contentColor = MaterialTheme.colorScheme.onSecondaryContainer
                            ) {
                                Icon(
                                    imageVector = if (isNearTop) Icons.Filled.KeyboardArrowDown else Icons.Filled.KeyboardArrowUp,
                                    contentDescription = stringResource(
                                        if (isNearTop) Res.string.scroll_to_bottom else Res.string.scroll_to_top
                                    )
                                )
                            }
                        }
                    }

                    //Scroll hide/show FAB
                    val scrollState = rememberScrollState()
                    val hideFabAtBottom by remember {
                        derivedStateOf { scrollState.maxValue > 0 && scrollState.value >= scrollState.maxValue }
                    }
                    
                    Box(modifier = Modifier.fillMaxSize()) {
                        Column(
                            modifier = Modifier
                                .fillMaxSize()
                                .verticalScroll(scrollState)
                                .padding(20.dp),
                            verticalArrangement = Arrangement.spacedBy(16.dp)
                        ) { }
                        
                        AnimatedVisibility(
                            visible = !hideFabAtBottom,
                            enter = fadeIn() + scaleIn(),
                            exit = fadeOut() + scaleOut()
                            modifier = Modifier
                                .align(Alignment.BottomEnd)
                                .padding(20.dp)
                        ) { }
                    }    
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun LazyStaggeredGridState.isScrollingForward(): Boolean = run {
                        var previousIndex by remember(this) { mutableIntStateOf(firstVisibleItemIndex) }
                        var previousScrollOffset by remember(this) { mutableIntStateOf(firstVisibleItemScrollOffset) }

                        remember(this) {
                            derivedStateOf {
                                if (previousIndex != firstVisibleItemIndex) {
                                    previousIndex >= firstVisibleItemIndex
                                } else {
                                    previousScrollOffset >= firstVisibleItemScrollOffset
                                }
                            }.also {
                                previousIndex = firstVisibleItemIndex
                                previousScrollOffset = firstVisibleItemScrollOffset
                            }
                        }.value
                    }

                    @Composable
                    fun LazyGridState.isScrollingForward(): Boolean = run {
                        var previousIndex by remember(this) { mutableIntStateOf(firstVisibleItemIndex) }
                        var previousScrollOffset by remember(this) { mutableIntStateOf(firstVisibleItemScrollOffset) }

                        remember(this) {
                            derivedStateOf {
                                if (previousIndex != firstVisibleItemIndex) {
                                    previousIndex >= firstVisibleItemIndex
                                } else {
                                    previousScrollOffset >= firstVisibleItemScrollOffset
                                }
                            }.also {
                                previousIndex = firstVisibleItemIndex
                                previousScrollOffset = firstVisibleItemScrollOffset
                            }
                        }.value
                    }

                    @Composable
                    fun LazyListState.isScrollingForward(): Boolean = run {
                        var previousIndex by remember(this) { mutableIntStateOf(firstVisibleItemIndex) }
                        var previousScrollOffset by remember(this) { mutableIntStateOf(firstVisibleItemScrollOffset) }

                        remember(this) {
                            derivedStateOf {
                                if (previousIndex != firstVisibleItemIndex) {
                                    previousIndex > firstVisibleItemIndex
                                } else {
                                    previousScrollOffset > firstVisibleItemScrollOffset
                                }
                            }.also {
                                previousIndex = firstVisibleItemIndex
                                previousScrollOffset = firstVisibleItemScrollOffset
                            }
                        }.value
                    }
                    
                    """.trimIndent()
            )

            KotlinCode(
                code = """
            //LaunchedEffect inside derivedStateOf
            //Scroll the hide searchbar

            val gridState = rememberLazyGridState()
            val isSearchBarVisible = remember { mutableStateOf(true) }
            var previousOffset by remember { mutableIntStateOf(0) }

            LaunchedEffect(remember { derivedStateOf { gridState.firstVisibleItemScrollOffset } }) {
                snapshotFlow {  gridState.firstVisibleItemScrollOffset to gridState.isScrollInProgress }
                    .collect { (offset, isScrolling) ->

                        if (isScrolling) {
                            if (offset > previousOffset) {
                                isSearchBarVisible.value = false
                            } else if (offset < previousOffset) {
                                isSearchBarVisible.value = true
                            }
                        }
                        previousOffset = offset
                    }
            }
            """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Calculated for every recomposition of the current composable
                    val calculatedState = calculate (state)

                    //Calculated when state changes, then always emits new value
                    val recalculatedState = remember (state) & calculate (state) }

                    //Calculated when state changes, emits new value if it has changed
                    val derivedState by remember { derivedStateOf { calculate(state) } }

                    @Composable
                    fun CreateUsernameDerived(threshold: Int) {
                        val username = remember { mutableStateOf("User") }
                        // Perfect!
                        val enabled = remember(threshold) { derivedStateOf { username.value.length > threshold } }
                        Column {

                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun MultiComponentWithDerivedState() {
                        val items = remember { mutableStateListOf(1, 2, 3) }
                        var counter by remember { mutableStateOf(0) }
                        var text by remember { mutableStateOf("") }
                        val sum by remember { derivedStateOf { items.sum() } }

                    Column {
                            Button(onClick = { items.add((1..10).random()) }) {
                                Text(text = "Add Item")
                            }
                            Text(text = sum)
                            Button(onClick = { counter++ }) {
                                Text(text = counter)
                            }
                            TextField(value = text, onValueChange = { text = it })
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun DerivedStateOfSum() {
                        val list = remember { listOf(1, 2, 3, 4) }
                        val sum = remember { derivedStateOf { list.sum() } }

                        Text(sum.value)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @ExperimentalFoundationApi
                    @Composable
                    fun UnsplashImageList(
                        imageList: Flow<PagingData<Photo>>
                    ) {
                        val list = imageList.collectAsLazyPagingItems()
                        val isListEmpty by remember { derivedStateOf { list.itemCount <= 0 } }
                        if (list.loadState.refresh is LoadState.Loading) {
                            LoadingView(modifier = Modifier.fillMaxSize())
                        } else {
                            Crossfade(targetState = isListEmpty, label = "") {
                                if (it) {
                                    EmptyListStateComponent(modifier = Modifier.fillMaxSize())
                                } else {

                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        const val DEFAULT_CAMERA_ZOOM = 15f
                        private const val DEFAULT_CAMERA_ZOOM_FOR_SELECTED_USER = 17f

                        val userLocation = remember(state.defaultCameraPosition) {
                            val location = state.defaultCameraPosition
                            LatLng(location?.latitude ?: 0.0, location?.longitude ?: 0.0)
                        }

                        val defaultCameraZoom =
                            if (userLocation.latitude == 0.0 && userLocation.longitude == 0.0) 0f else DEFAULT_CAMERA_ZOOM

                        val cameraPositionState = rememberCameraPositionState {
                            position = CameraPosition.fromLatLngZoom(userLocation, defaultCameraZoom)
                        }

                        val relocate by remember {
                            derivedStateOf {
                                val distance = cameraPositionState.position.target.distanceTo(userLocation)
                                distance > 100
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Example 1
                    @Composable
                    fun DerivedStateExample(numbers: List<Int>) {
                        val totalSum by remember {
                            derivedStateOf { numbers.sum() }
                        }
                        Text(totalSum)
                    }

                    //Example 2
                    @Composable
                    fun FilteredListExample(items: List<String>) {
                        var query by remember { mutableStateOf("") }

                        // Create a derived state that filters the list based on the query
                        val filteredItems by remember {
                            derivedStateOf {
                                if (query.isEmpty()) {
                                    items
                                } else {
                                    items.filter { it.contains(query, ignoreCase = true) }
                                }
                            }
                        }

                        Column(modifier = Modifier.padding(16.dp)) {
                            TextField(
                                value = query,
                                onValueChange = { query = it },
                                label = { Text("Search") }
                            )
                            Spacer(modifier = Modifier.height(8.dp))
                            Text("Filtered Items:", style = MaterialTheme.typography.h6)
                            Spacer(modifier = Modifier.height(8.dp))

                            // Display the filtered list
                            LazyColumn {
                                items(filteredItems) { item ->
                                    Text(item, modifier = Modifier.padding(vertical = 4.dp))
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun MyComponent() {
                        var phoneNumber by remember { mutableStateOf("") }

                        val isValidPhoneNumber = remember {
                            derivedStateOf {
                                isPhoneNumberValid(phoneNumber)
                            }
                        }
                    }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                   fun MyComposable(value: Int) {
                       // Create a state variable that stores the user's name.
                       val nameState = remember { mutableStateOf("") }

                       // Create a new state variable that stores the user's name in uppercase.
                       val nameInUpperCaseState = remember { derivedStateOf {
                           // Convert the user's name to uppercase.
                           return it.value.uppercase()
                         }
                       }

                       // Display the user's name in uppercase.
                       Text(text = nameInUpperCaseState.value)
                   }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    enum class SortOrder { Name, Length, Reverse }

                    val sortOrder = remember { mutableStateOf(SortOrder.Name) }
                    val items = remember { mutableStateListOf("apple", "banana", "cherry", "date", "elderberry") }

                    val sortedItems = derivedStateOf {
                        when (sortOrder.value) {
                            SortOrder.Name -> items.sorted()
                            SortOrder.Length -> items.sortedBy { it.length }
                            SortOrder.Reverse -> items.sorted().reversed()
                        }
                    }

                    val itemColor: @Composable (String) -> Color = { item ->
                        when (sortedItems.value.indexOf(item) % 2) {
                            0 -> Color.White
                            1 -> Color.LightGray
                            else -> Color.Transparent
                        }
                    }

                    Column {
                        Row {
                            Button(onClick = { sortOrder.value = SortOrder.Name }) { Text("Sort by Name") }
                            Button(onClick = { sortOrder.value = SortOrder.Length }) { Text("Sort by Length") }
                            Button(onClick = { sortOrder.value = SortOrder.Reverse }) { Text("Reverse") }
                        }
                        LazyColumn {
                            items(sortedItems.value) { item ->
                                Text(
                                    text = item,
                                    color = itemColor(item),
                                    modifier = Modifier.padding(16.dp)
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun ConditionalRenderingExample() {
                        var isLoggedIn by remember { mutableStateOf(false) }

                        val buttonText by remember {
                            derivedStateOf {
                                if (isLoggedIn) "Logout" else "Login"
                            }
                        }

                        Button(onClick = { isLoggedIn = !isLoggedIn }) {
                            Text(text = buttonText)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Multiple State

                    @Composable
                    fun CombinedStateExample() {
                        var count by remember { mutableStateOf(0) }
                        var threshold by remember { mutableStateOf(10) }

                        val isThresholdReached by remember {
                            derivedStateOf {
                                count >= threshold
                            }
                        }

                        Text(
                            text = if (isThresholdReached) "Threshold Reached" else "Threshold Not Reached"
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //List Transformations

                    @Composable
                    fun TaskList(tasks: List<Task>) {
                        // Primary state
                        val taskList by remember { mutableStateOf(tasks) }

                        // Derived state - calculated from taskList
                        val completedTasks = taskList.filter { it.isCompleted }
                        val completionPercentage = if (taskList.isNotEmpty()) {
                            (completedTasks.size * 100 / taskList.size)
                        } else {
                            0
                        }

                        Column {
                            Text("Completion: $completionPercentage%")
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Filter List

                    val searchQuery = remember { mutableStateOf("") }
                    val items = remember { mutableStateOf(listOf("Apple", "Banana", "Cherry")) }

                    val filteredItems by remember {
                        derivedStateOf {
                            items.value.filter {
                                it.contains(searchQuery.value, ignoreCase = true)
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Validation

                    //Direct state check - evaluated on EVERY recomposition
                    val isUsernameValid = username.length >= 3

                    //Derived state - only recalculated when username changes.
                    //Calculate ONCE, then reuse everywhere
                    val isUsernameValidOptimized by remember {
                        derivedStateOf { username.length >= 3 }
                    }

                    if (!isUsernameValid && username.isNotBlank()) {
                        Text("Username must be at least 3 characters")
                    }

                    val email = remember { mutableStateOf("") }
                    val isEmailValid by remember {
                        derivedStateOf {
                            android.util.Patterns.EMAIL_ADDRESS.matcher(email.value).matches()
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Computed UI States

                    val isLoading = remember { mutableStateOf(false) }
                    val hasError = remember { mutableStateOf(false) }
                    val data = remember { mutableStateOf<List<Item>?>(null) }

                    val uiState by remember {
                        derivedStateOf {
                            when {
                                isLoading.value -> UiState.Loading
                                hasError.value -> UiState.Error
                                data.value != null -> UiState.Success(data.value!!)
                                else -> UiState.Initial
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun UserDetailsOptimized(user: User) {
                        // Calculations wrapped in derivedStateOf
                        val isAdmin by remember { derivedStateOf { user.role == "ADMIN" } }
                        val canEdit by remember { derivedStateOf { user.permissions.contains("EDIT") } }

                        Column {
                            // These sections will ONLY recompose when isAdmin or canEdit
                            // actually change, not on every recomposition of UserDetailsOptimized
                            if (isAdmin) { AdminPanel() }
                            if (canEdit) { EditButton() }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Hybrid Approach

                    //ViewModel exposes transformed state using Flow operators
                    val isFormValid = combine(email, password) { email, password ->
                        isValidEmail(email) && isValidPassword(password)
                    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(), false)

                    //UI applies additional derivations as needed
                    @Composable
                    fun LoginButton(isFormValid: Boolean) {
                        val buttonEnabled by remember {
                            derivedStateOf { isFormValid && !isOffline }
                        }

                        Button(enabled = buttonEnabled) { /* ... */ }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //Do I need to use derivedStateOf to combine multiple states together?
                    //derivedStateOf also isn’t helping with asynchronous updates, the Compose state snapshot system is dealing with that separately and these calls here are synchronous.

                    var firstName by remember { mutableStateOf("") }
                    var lastName by remember { mutableStateOf("") }

                    // This derivedStateOf is redundant
                    val fullName = remember { derivedStateOf { "$firstName $lastName" } }
                    """.trimIndent()
            )

            //https://github.com/kagg886/Pixiv-MultiPlatform
            //https://github.com/darriousliu/PiPixiv
            KotlinCode(
                code = """
                    //Favorite ProgressBar

                    FavoriteButton(
                        isFavorite = ,
                        modifier = Modifier.size(30.dp),
                        onDoubleClick = {
                            betterFavoriteDialog = true
                        }
                    ) {
                        if (it == FavoriteState.Favorite) {
                            return@FavoriteButton
                        }
                        if (it == FavoriteState.NotFavorite) {
                            return@FavoriteButton
                        }
                    }

                    sealed class FavoriteState {
                        data object Favorite : FavoriteState()
                        data object Loading  : FavoriteState()
                        data object NotFavorite : FavoriteState()
                    }

                    @Composable
                    fun FavoriteButton(
                        modifier: Modifier = Modifier,
                        isFavorite: Boolean,
                        nonFavoriteIcon: @Composable () -> Unit = {
                            Icon(imageVector = Icons.Default.FavoriteBorder, contentDescription = null)
                        },
                        favoriteIcon: @Composable () -> Unit = {
                            Icon(imageVector = Icons.Default.Favorite, contentDescription = null, tint = Color.Red)
                        },
                        onDoubleClick: suspend () -> Unit = {},
                        onModify: suspend (target: FavoriteState) -> Unit,
                    ) {
                        var loading by remember { mutableStateOf(false) }
                        val state by remember(isFavorite) {
                            derivedStateOf {
                                when {
                                    loading -> FavoriteState.Loading
                                    isFavorite -> FavoriteState.Favorite
                                    else -> FavoriteState.NotFavorite
                                }
                            }
                        }
                        val scope = rememberCoroutineScope()
                        AnimatedContent(
                            targetState = state,
                            modifier = modifier.size(30.dp)
                        ) {
                            when (it) {
                                FavoriteState.Favorite -> {
                                    IconButton(
                                        onClick = {
                                            loading = true
                                            scope.launch {
                                                onModify(FavoriteState.NotFavorite)
                                            }.invokeOnCompletion {
                                                loading = false
                                            }
                                        }
                                    ) {
                                        favoriteIcon()
                                    }
                                }

                                FavoriteState.Loading -> {
                                    CircularProgressIndicator()
                                }

                                FavoriteState.NotFavorite -> {
                                    val interactionSource = remember { MutableInteractionSource() }
                                    val viewConfiguration = LocalViewConfiguration.current

                                    LaunchedEffect(interactionSource) {
                                        var isLongClick = false

                                        interactionSource.interactions.collectLatest { interaction ->
                                            when (interaction) {
                                                is PressInteraction.Press -> {
                                                    isLongClick = false
                                                    delay(viewConfiguration.longPressTimeoutMillis)
                                                    isLongClick = true
                                                    onDoubleClick()
                                                }

                                                is PressInteraction.Release -> {
                                                    if (isLongClick.not()) {
                                                        loading = true
                                                        scope.launch {
                                                            onModify(FavoriteState.Favorite)
                                                        }.invokeOnCompletion {
                                                            loading = false
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }

                                    IconButton(
                                        interactionSource = interactionSource,
                                        onClick = {}
                                    ) {
                                        nonFavoriteIcon()
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/terrakok/WikWok
            KotlinCode(
                code = """
                    //Pagination - TickTok UI

                    @Composable
                    fun WikipediaScreen(
                        navController: NavController
                    ) {
                        val viewModel = viewModel { WikipediaViewModel(wikipediaService, likedArticlesStore, settings) }
                        val uiState by viewModel.uiState.collectAsStateWithLifecycle()
                        val pagerState = rememberPagerState { uiState.articles.size + 1 }

                        // Check if we need to load more articles when user scrolls to the end
                        val shouldLoadMore = remember {
                            derivedStateOf {
                                // Load more when we're 3 pages away from the end
                                pagerState.currentPage >= uiState.articles.size - 3
                            }
                        }

                        // Load more articles when needed
                        LaunchedEffect(shouldLoadMore.value) {
                            if (shouldLoadMore.value && !uiState.isLoading) {
                                //Load more function call here
                            }
                        }

                        // Update pager size when articles change
                        LaunchedEffect(uiState.articles.size) {
                            if (uiState.articles.isNotEmpty()) {
                                pagerState.animateScrollToPage(
                                    pagerState.currentPage.coerceAtMost(uiState.articles.size - 1)
                                )
                            } else {
                                pagerState.scrollToPage(0)
                            }
                        }

                        Box(
                            modifier = Modifier.fillMaxSize()
                        ) {
                            // TikTok-like fullscreen pager using VerticalPager
                            VerticalPager(
                                state = pagerState,
                                modifier = Modifier.fillMaxSize()
                            ) { pageIndex ->
                                if (pageIndex < uiState.articles.size) {
                                    //ItemView
                                } else if (uiState.isLoading) {
                                    Box(
                                        modifier = Modifier.fillMaxSize(),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        CircularProgressIndicator(
                                            color = MaterialTheme.colorScheme.primary,
                                            modifier = Modifier.size(60.dp)
                                        )
                                    }
                                }
                            }
                        }
                    }

                    data class WikipediaUiState(
                        val articles: List<WikipediaArticle> = emptyList(),
                        val isLoading: Boolean = false,
                        val error: String? = null
                    )

                    @Composable
                    fun WikipediaArticleItem(
                        article: WikipediaArticle,
                        isLiked: Boolean,
                        onLikeClick: () -> Unit,
                        modifier: Modifier = Modifier,
                    ) {
                        Box(modifier = modifier) {
                            AsyncImage(
                                model = article.thumbnail,
                                contentDescription = null,
                                contentScale = ContentScale.Crop,
                                clipToBounds = true,
                                imageLoader = LocalImageLoader.current,
                                modifier = Modifier.fillMaxSize(),
                            )
                            // Main content
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .align(Alignment.BottomStart)
                                    .background(Color.Black.copy(alpha = 0.7f))
                                    .windowInsetsPadding(
                                        WindowInsets.safeDrawing.only(WindowInsetsSides.Bottom + WindowInsetsSides.Horizontal)
                                    )
                                    .padding(16.dp)
                            ) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text(
                                        text = article.title,
                                        color = Color.White,
                                        fontSize = 20.sp,
                                        fontWeight = FontWeight.Bold,
                                        modifier = Modifier.weight(1f)
                                    )
                                    Row {
                                        IconButton(onClick = onLikeClick) {
                                            Icon(
                                                modifier = Modifier.size(24.dp),
                                                imageVector =
                                                    if (isLiked) vectorResource(Res.drawable.ic_favorite_fill)
                                                    else vectorResource(Res.drawable.ic_favorite),
                                                contentDescription = if (isLiked) "Unlike" else "Like",
                                                tint = if (isLiked) Color.Red else Color.White
                                            )
                                        }
                                        val shareService = LocalShareService.current
                                        IconButton(
                                            onClick = { shareService.share(article.url) }
                                        ) {
                                            Icon(
                                                modifier = Modifier.size(24.dp),
                                                imageVector = vectorResource(Res.drawable.ic_share),
                                                contentDescription = "Share",
                                                tint = Color.White
                                            )
                                        }
                                    }
                                }
                                Spacer(modifier = Modifier.height(8.dp))
                                Text(
                                    text = article.extract,
                                    color = Color.White.copy(alpha = 0.8f), // Slightly less opaque white
                                    fontSize = 14.sp,
                                    maxLines = 15,
                                    overflow = TextOverflow.Ellipsis
                                )
                                Spacer(modifier = Modifier.height(16.dp))

                                val uriHandler = LocalUriHandler.current
                                OutlinedButton(
                                    modifier = Modifier.align(Alignment.End),
                                    onClick = { uriHandler.openUri(article.url) }
                                ) {
                                    Row(
                                        verticalAlignment = Alignment.CenterVertically,
                                    ) {
                                        Text(
                                            text = "Read more", // Unicode right arrow
                                            color = Color.White,
                                            fontWeight = FontWeight.Bold
                                        )
                                        Icon(
                                            imageVector = vectorResource(Res.drawable.ic_arrow_right),
                                            contentDescription = "Read more",
                                            tint = Color.White,
                                            modifier = Modifier.size(16.dp).padding(start = 4.dp, top = 2.dp),
                                        )
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "derivedStateOf convert one or multiple state objects into another state.",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Approach1: userName is recalculated on every recomposition, even if loggedIn is unchanged (performance issue).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Approach2: LaunchedEffect adds unnecessary complexity for updating userName.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //Wrong
                    var loggedIn by remember { mutableState0f(false) 3
                    var userName = if (loggedIn) getUserName() else "Guest"

                    //Good
                    var loggedIn by remember & mutableState0f(false) }
                    var userName by remember { mutableState0f("") }
                    LaunchedEffect (loggedIn) {
                        userName = if (loggedIn) getUserName () else "Guest"
                    }

                    //Better
                    var loggedIn by remember & mutableState0f(false) 3
                    val userName by remember {
                        derivedStateOf { if (loggedIn) getUserName else "Guest" ]
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "How is it different from remember?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember stores a value across recompositions but does not automatically recalculate when inputs change.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "derivedStateOf recalculates when the state it reads actually changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What does derivedStateOf track internally?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It tracks snapshot reads.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This means it observes only the states used inside its block and invalidates only when those states change.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How does derivedStateOf reduce recomposition in LazyColumn?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LazyColumn only recomposes when the derived value changes. Instead of recalculating large filtered/sorted lists repeatedly, Compose updates only when inputs change.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "When should we use StateFlow transformations instead of derivedStateOf?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If the logic is part of the business layer or ViewModel, use: map {}, combine {}",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why do we wrap derivedStateOf inside remember?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Because without remember, the derivedStateOf object gets created on every recomposition, which gives no performance benefit.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "remember keeps the derived state stable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "when to use derivedStateOf and when to use remember(key) { calculation }.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use remember(key) when you want to cache an expensive computation (sorting, filtering) that should rerun when its inputs change. Use derivedStateOf when the source changes more frequently than you need to recompose.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    // remember(key): Recalculates when the KEY changes
                    val sortedProducts = remember(products, sortOrder) {
                        products.sortedBy { if (sortOrder == Ascending) it.price else -it.price }
                    }

                    // derivedStateOf: Recalculates when the RESULT changes
                    val showScrollToTop by remember {
                        derivedStateOf { listState.firstVisibleItemIndex > 5 }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Do I always have to use remember with derivedStateOf?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Yes, absolutely. The remember function is essential. derivedStateOf returns a State object which needs to be kept in memory across recompositions. Without remember, a new derivedStateOf object would be created on every recomposition, completely defeating the purpose of its internal caching and dependency tracking.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Does derivedStateOf have to be remembered?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If it is inside a Composable function, yes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "derivedStateOf is just like mutableStateOf or any other object that needs to survive recomposition. If you use it inside a composable function then it should be wrapped in a remember or else it will be reallocated on every recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "derivedStateOf to Limit Recomposition",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If you have a calculated value that depends on a frequently changing state, use derivedStateOf. It only triggers recomposition if the result of the calculation changes, not every time the source state changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    val scrollState = rememberScrollState()

                    // This state only updates (and recomposes the dependent composables) 
                    // when the boolean result of the calculation changes, not on every pixel scroll.
                    val isScrolledToTop by remember { 
                        derivedStateOf { 
                            scrollState.value == 0 
                        }
                    }
                    // Now, a component that uses 'isScrolledToTop' recomposes far less often.
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun MyComp() {
                      // We need to use remember here to survive recomposition
                      val state = remember { derivedStateOf { … } }
                    }

                    class MyViewModel: ViewModel() {
                      // We don't need remember here (nor could we use it) because the ViewModel is
                      // outside of Composition.
                      val state = derivedStateOf { … }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "DerivedStateObserver",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A DerivedStateObserver is an internal Compose runtime class that tracks dependencies between states and derived states.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When a derivedStateOf block runs, Compose:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Starts a DerivedState observation scope.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Tracks which State objects are read inside.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "When any of those source states change, it marks the derived state as invalid (i.e., needs recomputation).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "observeDerivedStateRecalculations",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Observe the recalculations performed by any derived state that is recalculated during the execution of block.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "observer - called for every calculation of a derived state in the block.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "block - the block of code to observe.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "When should I use derivedStateOf instead of just calculating the value directly?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The value you are calculating (Derived State) is based on one or more Source States. But only recomposes when the derived value actually changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Source State(s) change very frequently (e.g., scroll position, a text field’s value on every keystroke).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The resulting Derived Value changes less frequently than the Source State(s).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If the calculation is cheap and the derived value changes just as often as the source state (e.g., val doubledCount = count.value * 2), you can just calculate it directly. derivedStateOf adds a small overhead, and you should only accept that cost if you gain performance benefits by reducing unnecessary recompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //1
                    val query = remember { mutableStateOf("") }
                    val items = remember { listOf("Apple", "Banana", "Cherry", "Date") }

                    val filteredItems = remember {
                        derivedStateOf {
                            items.filter { it.contains(query.value, ignoreCase = true) }
                        }
                    }
                    
                    //2
                    val count = remember { mutableStateOf(0) }
                    val factor = remember { mutableStateOf(2) }

                    val result = remember {
                        derivedStateOf { count.value * factor.value }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Can derivedStateOf be used outside of a @Composable function?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The derivedStateOf function itself can be used outside a Composable, but the State objects it reads must be Compose States (like mutableStateOf or a StateFlow.collectAsState()). This allows Compose's snapshot system to track the dependencies and trigger the recalculation and subsequent recomposition when needed. If the value it's reading isn't a Compose State, it won't react to changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Do I ever need to use remember(key) and derivedStateOf together? When is this needed?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "derivedStateOf can only update when it reads a Compose state object. Any other variables read inside derivedStateOf will capture the initial value of that variable when the derived state is created.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If you need to use those variables in your calculation, then you can provide them as a key to your remember function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "threshold derivedStateOf",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //Calculated when state changes, then always emits new value
                    val recalculatedState = remember(state) { calculate(state) }
                    
                    //Calculated when state changes, emits new value if it has changed
                    val derivedState by remember { derivedStateOf { calculate (state) }}
                    
                    //derivedStateOf body can only observe state, it cannot observe other values! If you use any value that is not a state, and that might change, you must include it as remember key to create new derived state when it changes!
                    @Composable
                    fun CreateUsernameDerived(threshold: Int) 
                    {
                        val username = remember { mutableStateOf("User") }
                        val enabled = remember(threshold) { derivedStateOf(username.value.Length > threshold }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "What’s the difference between remember(key) and derivedStateOf?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "derivedStateOf {} is used when your state or key is changing more than you want to update your UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Now, let’s say we have an expensive function that calculates something for us with a parameter. We want our UI to recompose any time the output of that function changes (importantly, the function is also idempotent). We use remember with a key here, as our UI needs to update just as much as our key changes. That is, we have the same amount of input and output.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    val output = remember(input) { expensiveCalculation(input) }
                    """.trimIndent()
            )

            SpanText(
                text = "How Derived State Works in Compose",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you call derivedStateOf { calculation }, Compose creates a special DerivedSnapshotState object. This object:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.8.em).toAttrs()) {
                        SpanText(
                            text = "Tracks dependencies automatically",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )

                        Ul {
                            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "When calculation runs, Compose records every State object it reads. In our example, it records listState.firstVisibleItemIndex.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.8.em).toAttrs()) {
                        SpanText(
                            text = "Caches the result",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )

                        Ul {
                            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "After the first calculation, the result is stored. Subsequent reads return the cached value without re-executing the calculation.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.8.em).toAttrs()) {
                        SpanText(
                            text = "Re-evaluates lazily",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )

                        Ul {
                            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "When a dependency changes, the derived state doesn’t immediately recalculate. Instead, it marks itself as potentially stale. Only when something actually reads the derived state does it re-execute the calculation.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.8.em).toAttrs()) {
                        SpanText(
                            text = "Compares results",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )

                        Ul {
                            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "After recalculation, the new result is compared with the previous result using the equality policy (default: structuralEqualityPolicy, which uses equals()). If they're equal, Compose does NOT trigger recomposition. If they're different, it does.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }
                        }
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "derivedStateOf is built on top of the snapshot system in Compose (androidx.compose.runtime.snapshots). This system tracks reads and writes to State objects, allowing Compose to automatically know when data changes. The derivedStateOf function takes a lambda block that computes the derived value, and it will only recompute the value when the state used inside the lambda changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why Use remember { derivedStateOf }?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It avoids recomputing the derived value unnecessarily.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It ensures that LaunchedEffect only triggers when the derived value genuinely changes, improving performance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Do I need to use derivedStateOf to combine multiple states together?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Most likely, no. If you have multiple states that are combining together to create a result then you probably want recomposition to happen any time one of them changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Take for example a form that takes in a first name and last name and displays a full name.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    var firstName by remember { mutableStateOf("") }
                    var lastName by remember { mutableStateOf("") }

                    val fullName = remember { derivedStateOf { "$firstName $lastName" } }
                    """.trimIndent()
            )

            SpanText(
                text = "Reading from one or more state objects.",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Performing operations to transform that data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Using the result in your UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The key performance benefit: When the original state changes, the derived value is recalculated automatically during recomposition without additional overhead.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }
        }
    }
}